import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { TrainingTopicDto } from '../../../api/types/dtos/training';
import { useUpdateTrainingTopic } from '../../../api/hooks/useTrainingTopics';
import { useOrganization } from '../../../contexts/OrganizationContext';

interface EditTopicModalProps {
  open: boolean;
  onClose: () => void;
  topic: TrainingTopicDto | null;
  trainingId: string;
  moduleId: string;
}

const EditTopicModal: React.FC<EditTopicModalProps> = ({
  open,
  onClose,
  topic,
  trainingId,
  moduleId,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sequence, setSequence] = useState(1);
  const [progressContribution, setProgressContribution] = useState(100);
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const updateMutation = useUpdateTrainingTopic(
    selectedOrganization?.id || '',
    trainingId,
    moduleId
  );

  useEffect(() => {
    if (topic) {
      setName(topic.name);
      setDescription(topic.description || '');
      setSequence(topic.sequence);
      setProgressContribution(topic.progressContribution);
    }
  }, [topic]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    if (!topic?.id) {
      enqueueSnackbar('No se puede actualizar el tema', { variant: 'error' });
      return;
    }

    if (progressContribution < 0 || progressContribution > 100) {
      enqueueSnackbar('La contribución al progreso debe estar entre 0 y 100', {
        variant: 'error',
      });
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: topic.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          sequence,
          progressContribution,
          moduleId: topic.moduleId,
          logo: topic.logo,
          isActive: topic.isActive,
        },
      });

      enqueueSnackbar('Tema actualizado exitosamente', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar('Error al actualizar el tema', { variant: 'error' });
      console.error('Error updating topic:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar Tema</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Secuencia"
            type="number"
            value={sequence}
            onChange={(e) => setSequence(parseInt(e.target.value) || 1)}
            required
            fullWidth
            InputProps={{ 
              inputProps: { 
                min: 1,
                onWheel: (e) => e.currentTarget.blur()
              } 
            }}
          />
          <TextField
            label="Contribución al Progreso (%)"
            type="number"
            value={progressContribution}
            onChange={(e) =>
              setProgressContribution(parseInt(e.target.value) || 0)
            }
            required
            fullWidth
            helperText="La suma de todos los temas del módulo debe ser 100%"
            InputProps={{ 
              inputProps: { 
                min: 0, 
                max: 100,
                onWheel: (e) => e.currentTarget.blur()
              } 
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateMutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateMutation.isPending}
          startIcon={updateMutation.isPending && <CircularProgress size={20} />}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTopicModal;



